import { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useDomainBranding } from "@/hooks/useDomainBranding";
import { buildCanonicalUrl } from "@/lib/canonicalize";
import { getHreflangAlternates } from "@/lib/hreflang";
import ctrOverrides from "@/data/seo-ctr-overrides.json";

type CtrOverride = { title?: string; description?: string };

/**
 * Pages that already rank in Google but earn ~0% CTR get a snippet written
 * against the exact query wording Search Console reports. The same map is
 * applied to the prerendered HTML in scripts/vite-plugin-prerender.mjs, so the
 * static and the rendered <head> always agree.
 */
const getCtrOverride = (pathname: string): CtrOverride | undefined => {
  const key =
    pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return (ctrOverrides as Record<string, CtrOverride>)[key];
};


interface DomainSEOProps {
  /** Page title — brand-default used if omitted */
  title?: string;
  /** Page description — brand-default used if omitted */
  description?: string;
  /** Path-only canonical (e.g. "/about"). Defaults to current pathname. */
  canonicalPath?: string;
  /** Full canonical URL override (takes precedence over canonicalPath) */
  canonicalUrl?: string;
  /** Open Graph image override */
  ogImage?: string;
  /** Optional extra <Helmet> children (JSON-LD scripts, etc.) */
  children?: ReactNode;
}

/**
 * Single source of truth for domain-aware <head> tags.
 * Picks the right title / description / canonical / og:* / twitter:*
 * for the active hostname (O'Brien vs RoofCertificateFlorida).
 */
const DomainSEO = ({
  title,
  description,
  canonicalPath,
  canonicalUrl,
  ogImage,
  children,
}: DomainSEOProps) => {
  const brand = useDomainBranding();
  let location: { pathname: string } = { pathname: "/" };
  try {
    // useLocation throws if not inside a Router; fall back gracefully.
    location = useLocation();
  } catch {
    // no-op
  }

  const override = getCtrOverride(location.pathname ?? "/");
  const finalTitle = override?.title || title || brand.defaultTitle;
  const finalDescription =
    override?.description || description || brand.defaultDescription;
  const finalImage = ogImage || brand.ogImage;


  const path = canonicalPath ?? location.pathname ?? "/";
  const finalCanonical =
    canonicalUrl || buildCanonicalUrl(brand.canonicalOrigin, path);
  const alternates = getHreflangAlternates(path);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={finalCanonical} />

      {/* hreflang — keeps /es from competing with its English twin */}
      {alternates.map((alt) => (
        <link
          key={alt.hreflang}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={buildCanonicalUrl(brand.canonicalOrigin, alt.path)}
        />
      ))}


      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={brand.siteName} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={finalImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {brand.twitterHandle && (
        <meta name="twitter:site" content={brand.twitterHandle} />
      )}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {children}
    </Helmet>
  );
};

export default DomainSEO;
